import io from 'socket.io-client';
import { showNotification, updateTotalPointsDisplay, showPopupPoints, clearLEDDisplay } from './notif.js';
import { showItemReceivedNotification, showOfferSentPopup, showOfferPopup, acceptTrade, declineTrade, showReceiveBuyOfferPopup, handleRejectBuyOffer, handleAcceptBuyOffer, handleBuyOfferAcceptedByOther, showSocialTradeForm , showSocialTradeDialog , showSocialTradeReceived , showSocialTradeResponse } from './offer.js';
import { showRedeemOfferPopup } from './redeemResult.js';
import { showTaxDecisionPopup, showPointsAssignedNotification, showtaxSingleItem, showPointsDeductedNotification, showItemSelectedAssign, showZollarSelectedAssign, displayNotification  } from './tax.js';
import { showMoodAnalysisPopup, triggerBurst } from './mood.js';
import { showMementoMoriAnnouncement, cleanupMementoMoriTimer } from './objectives.js';
import { fetchStocks, displayStocks, openStockTradeModal, showWaitingForResponsePopup, closeWaitingForResponsePopup, showStockBidPopup } from './stockTrade.js';
import { fetchBondData, placeBondBid, buyBond, showBondBidPopup } from './bondTrade.js';
import { showCommunityInvitationPopup, handleResponseCM, startCommunityTimer, showCommunityInterface, leaveCommunity, handleCreateCommunity, initializeCreateCommunityButton, checkAndShowCommunityInterface } from './community.js';
import { showMessagePopup, sendMessage, displayReceivedMessage, openBotChat } from './messageSend.js';
import { playPubVideo, playZnewsVideo,  initializeLBOButton, handleLBOButtonClick, handleLBOUpdateForTarget, handleSubsidyUpdateForTarget, handleSubsidyReceived,  displayDeletePortfolioNotification, displayDeletePortfolioUpdate, displayDeletePortfolioButtonReceived, handleItemStolen, handleSpecialActionNotification  } from './button.js';
import { cleanupSimulationTimer } from './simulationTimer.js';
import { showDisplayedArtwork } from './artViewer.js';
import EmotionalDebtMeter from './emotionalDebtMeter.js';
import { qualitativeFromFraction_LOCAL , modalManager , handleCDSVictimCollapse, handleCDSExploiterGuilt, handleCDSInitiation } from './socialEconomyHandlers.js';
import { apiBaseSocket, apiBaseUrl } from './config.js';
import { t, confirmT, updateElementText } from './translationHelper.js';
import translations from './translations.js';
import { showHoloModal, showHoloConfirm, holoNotify } from './holoModal.js';
import { registerDebtPopupHandlers } from './debtPopupHandlers.js';


let socket;
let isProcessingMessage = false;
const messageQueue = [];
let reconnectTimeout;
let isConnected = false;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY = 3000;

// Track attached handlers for cleanup
let handlersAttached = false;
let emotionalMeter = null;
let beforeUnloadHandler = null;

class MessageQueue {
  constructor() {
	this.queue = [];
	this.isProcessing = false;
	this.maxSize = 50;
  }

  enqueue(message) {
	if (this.queue.length >= this.maxSize) {
	  const removed = this.queue.shift();
	  console.warn('⚠️ MessageQueue full - dropped oldest message:', removed);
	}
	this.queue.push(message);
	this.processQueue();
  }

  async processQueue() {
	if (this.isProcessing || this.queue.length === 0 || !socket || !socket.connected) return;

	this.isProcessing = true;
	while (this.queue.length > 0 && socket.connected) {
	  const message = this.queue.shift();
	  try {
		await this.sendMessage(message);
	  } catch (error) {
		console.error('Error sending message:', error);
		this.queue.unshift(message);
		break;
	  }
	}
	this.isProcessing = false;
  }

  sendMessage(message) {
	  return new Promise((resolve, reject) => {
		socket.emit('message', message, (error) => {
		  if (error) {
			reject(error);
		  } else {
			resolve();
		  }
		});
	  });
	}
	
  clear() {
	this.queue = [];
	this.isProcessing = false;
  }
  }
  
  const clientMessageQueue = new MessageQueue();

function getCookie(name) {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) return parts.pop().split(';').shift();
  }
  
  export function getPortfolioId() {
	return getCookie('portfolioId') || localStorage.getItem('portfolioId');
  }

/**
 * A trigger (steal / ransom / LBO / subsidy / dark pool / communist assault) has
 * just been granted to this trader mid-session.
 *
 * These five socket handlers used to call displayDeletePortfolioButton(true),
 * displayLBOButton(true) &c. directly — but those functions are module-private
 * inner functions of trader.js and were never imported here, so every call was
 * an unresolved identifier that threw ReferenceError. A trigger granted during
 * a session therefore never appeared on ANY page until a manual reload.
 *
 * Announcing it instead lets each interface react in its own way: trader.js
 * calls its local display*Button(true), and zfloor.js refetches the portfolio
 * (the has*Button flags are authoritative there) and re-runs its armed check.
 */
function announceToolGranted(toolId) {
  document.dispatchEvent(new CustomEvent('zx:toolGranted', { detail: toolId }));
}

/**
 * Reset socket state for fresh simulation run
 * Call this before starting a new simulation to prevent stale state
 */
export function resetSocket() {
  console.log('🔄 [resetSocket] Resetting socket state for fresh simulation...');

  // Clean up emotional meter
  if (emotionalMeter) {
	emotionalMeter = null;
  }

  // Remove beforeunload handler
  if (beforeUnloadHandler) {
	window.removeEventListener('beforeunload', beforeUnloadHandler);
	beforeUnloadHandler = null;
  }

  if (socket) {
	socket.removeAllListeners();
	if (socket.connected) {
	  socket.disconnect();
	}
	socket = null;
  }

  // Reset module state
  isConnected = false;
  reconnectAttempts = 0;
  handlersAttached = false;
  
  if (reconnectTimeout) {
	clearTimeout(reconnectTimeout);
	reconnectTimeout = null;
  }

  // Clear message queues
  clientMessageQueue.clear();
  messageQueue.length = 0;
  isProcessingMessage = false;

  // Remove any lingering popup/modal elements
  const popupSelectors = [
	'#eulogy-loading-popup',
	'#phase-transition-modal',
	'.simulation-warning',
	'#simulationTimer'
  ];

  popupSelectors.forEach(selector => {
	const element = document.querySelector(selector);
	if (element) {
	  element.remove();
	  console.log(`🧹 [resetSocket] Removed lingering element: ${selector}`);
	}
  });

  console.log('✅ [resetSocket] Socket state reset complete');
}

export function connectWebSocket(botToken = null, botPortfolioId = null, botSessionId = null ) {
  if (socket && (socket.connected || socket.connecting)) {
	console.log('WebSocket is already connected or connecting.');
	return;
  }
  
  const portfolioId = botPortfolioId || getPortfolioId();
  const token = botToken || getCookie('token') || localStorage.getItem('token');
  const sessionId = localStorage.getItem('sessionId');
  const hyperUserPortfolioId = process.env.HYPERUSER_PORTFOLIOID;
  
  const isHyperUser = portfolioId === hyperUserPortfolioId;
  const isBot = !!(botToken && botPortfolioId);
  
  console.log('🔌 Connecting WebSocket:', {
	  isBot,
	  portfolioId: portfolioId?.substring(0, 8) + '...',
	  hasToken: !!token,
	  sessionId: sessionId?.substring(0, 8) + '...'
  });
  
  if (!isHyperUser && (!portfolioId || !token || !sessionId)) {
	console.error('No portfolio ID, token, or session ID found. Cannot connect to WebSocket.');
	return;
  }
  
  socket = io(`${apiBaseSocket}`, { 
	query: isHyperUser
	  ? { userId: portfolioId }
	  : { 
		  userId: portfolioId, 
		  token: token,
		  sessionId: sessionId,
		  isBot: isBot
		},
	transports: ['websocket'],
	reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
	reconnectionDelay: 1000,
	timeout: 10000
  });
  
  socket.on('connect', () => {
	console.log('Connected to WebSocket');
	isConnected = true;
	reconnectAttempts = 0;

	window.popupTrackerSocket = socket;
	window.socket = socket;

	if (window.popupTracker) {
	  window.popupTracker.initialize(socket);
	  console.log('🔗 [SocketU] Initialized popup tracker with socket connection');
	}
	
	if (isBot) {
	  console.log(`🤖 Registering bot: ${botPortfolioId}`);
	  socket.emit('registerBot', {
		portfolioId: botPortfolioId,
		token: botToken,
		socketId: socket.id
	  });
	} else {
	  localStorage.setItem('socketId', socket.id);
	}
	
	clientMessageQueue.processQueue();
	
	// Attach handlers only once per socket instance
	if (!handlersAttached) {
	  attachSocketHandlers(socket, isBot, portfolioId);
	  handlersAttached = true;
	}
  });
	

  socket.on('connect_error', (error) => {
	 console.error('WebSocket connection error:', error);
	 handleReconnect();
   });
 
   socket.on('disconnect', (reason) => {
	 console.log(`WebSocket connection closed: ${reason}`);
	 isConnected = false;
   });
}

/**
 * Attach all socket event handlers - called only once per connection
 */
function attachSocketHandlers(socket, isBot, portfolioId) {
  console.log('🔗 [SocketU] Attaching socket handlers...');

socket.on('simulation:ended', (payload) => {
	 const isSimulated = localStorage.getItem('isSimulated') === 'true';
	 const simulationType = localStorage.getItem('simulationType');
   
	 if (isSimulated || simulationType) {
	   console.log('🎯 Simulation ended for simulated user:', payload);
	   window.dispatchEvent(new CustomEvent('simulation-ended', { detail: { payload } }));
	 } else {
	   console.log('🛡️ Ignored simulation:ended event for normal user.');
	 }
   });
   

  socket.on('receiveMessage', (message) => {
	if (message.senderName && (message.senderName.includes('_') || message.senderName.match(/\d{4}$/))) {
	  message.isBot = true;
	}
	
	if (message.botData && message.botData._id) {
	  const currentUserId = localStorage.getItem('portfolioId');
	  if (currentUserId) {
		const storageKey = `botMessage_${message.botData._id}_${currentUserId}`;
		const messageData = {
		  content: message.content,
		  botData: message.botData,
		  tradingContext: message.tradingContext,
		  recommendedAction: message.recommendedAction,
		  specificStock: message.specificStock,
		  specificItem: message.specificItem,
		  tradingLinks: message.tradingLinks,
		  timestamp: Date.now()
		};
		localStorage.setItem(storageKey, JSON.stringify(messageData));
		console.log('💾 Stored bot message in localStorage:', storageKey);
	  }
	}
	
	displayReceivedMessage(message);
	
	// FIX: Use setTimeout cleanup instead of creating orphan DOM elements
	const messageElement = document.createElement('div');
	messageElement.innerText = message.content;
	document.body.appendChild(messageElement);
	
	const cleanupTimer = setTimeout(() => {
	  if (messageElement && messageElement.parentNode) {
		messageElement.parentNode.removeChild(messageElement);
	  }
	}, 5000);
	
	// Store timer reference for potential cleanup
	messageElement._cleanupTimer = cleanupTimer;
  });
  
  socket.on('messageError', (error) => {
	showNotification(`${t('socketFront.messageError', 'Error sending message:')} ${error}`, false);
  });


  socket.on('zollarAssigned', (data) => {
	console.log('Received zollarAssigned event:', data);
	const portfolioId = data.portfolioId || null;
	showZollarSelectedAssign([portfolioId], data.message);
  });
  
  socket.on('cdsEvent', (payload) => {
	try {
	  console.log('📡 CDS Event Received:', payload);
  
	  if (!payload || !payload.data) return;
  
	  const { role, phase, data } = payload;
  
	  if (role === 'victim' && phase === 'collapse') {
		handleCDSVictimCollapse(data);
	  } else if (role === 'exploiter' && phase === 'collapse') {
		handleCDSExploiterGuilt(data);
	  } else if (phase === 'init') {
		handleCDSInitiation(data);
	  } else {
		console.warn('⚠️ Unknown CDS phase:', phase);
	  }
	} catch (err) {
	  console.error('❌ Error handling cdsEvent:', err);
	}
  });

 // === Emotional Updates ===
 socket.on('emotionalUpdate', async ({ sessionId }) => {
   const portfolioId = localStorage.getItem('portfolioId');
   if (!portfolioId) return;
 
   if (!emotionalMeter) {
	 emotionalMeter = new EmotionalDebtMeter('emotional-debt-meter', portfolioId);
	 await emotionalMeter.init();
   } else {
	 await emotionalMeter.fetchData();
   }
 
   console.log(`💫 Emotional debt meter updated for session ${sessionId}`);
 });
 
 // === Social Economy Requests ===
 socket.on('socialEconomyRequest', (data) => {
   console.log('Social economy request received:', data);
   const { type, fromName, requesterName, amount, humanInterest, package: pkg, vulnerabilityReason, vulnerabilityScore, abstractRequest, principalType } = data;

   const sender = fromName || requesterName || 'Someone';
   let msg, title, modalType;

   const getQualitativeDescriptor = (quantity, max) => {
	 const q = Number(quantity) || 0;
	 const m = Number(max);
	 if (q <= 0) return 'None';
	 if (m && m > 0) {
	   const r = q / m;
	   if (r <= 0.01)  return 'None';
	   if (r <= 0.03)  return 'Barely Any';
	   if (r <= 0.07)  return 'A Trace';
	   if (r <= 0.12)  return 'A Hint';
	   if (r <= 0.18)  return 'A Little';
	   if (r <= 0.26)  return 'A Few';
	   if (r <= 0.33)  return 'Some';
	   if (r <= 0.40)  return 'Several';
	   if (r <= 0.50)  return 'Moderate';
	   if (r <= 0.58)  return 'Noticeable';
	   if (r <= 0.65)  return 'Considerable';
	   if (r <= 0.72)  return 'Many';
	   if (r <= 0.78)  return 'A Lot';
	   if (r <= 0.83)  return 'Abundant';
	   if (r <= 0.88)  return 'Heaps';
	   if (r <= 0.92)  return 'A Great Deal';
	   if (r <= 0.95)  return 'Almost All';
	   if (r <= 0.97)  return 'Nearly Everything';
	   if (r <= 0.985) return 'Overflowing';
	   if (r <= 0.995) return 'Virtually Everything';
	   return 'All You Have';
	 }

	 if (q <= 0)   return 'None';
	 if (q <= 1)   return 'Barely Any';
	 if (q <= 2)   return 'A Trace';
	 if (q <= 3)   return 'A Hint';
	 if (q <= 4)   return 'A Little';
	 if (q <= 6)   return 'A Few';
	 if (q <= 9)   return 'Some';
	 if (q <= 12)  return 'Several';
	 if (q <= 16)  return 'Moderate';
	 if (q <= 20)  return 'Noticeable';
	 if (q <= 26)  return 'Considerable';
	 if (q <= 33)  return 'Many';
	 if (q <= 40)  return 'A Lot';
	 if (q <= 55)  return 'Abundant';
	 if (q <= 70)  return 'Heaps';
	 if (q <= 90)  return 'A Great Deal';
	 if (q <= 120) return 'Substantial';
	 if (q <= 160) return 'Ample';
	 if (q <= 210) return 'Extensive';
	 if (q <= 260) return 'Vast';
	 if (q <= 320) return 'Profuse';
	 if (q <= 380) return 'Almost All';
	 if (q <= 440) return 'Nearly Everything';
	 if (q <= 490) return 'Virtually Everything';
	 return 'All You Have';
   };

   const qLabel = getQualitativeDescriptor;

   if (type === 'help') {
	 title = 'Gift Offer';
	 const zCount = pkg?.zollars
		 ? (typeof pkg.zollars === 'number'
			 ? pkg.zollars
			 : Object.values(pkg.zollars || {}).reduce((a, b) => a + (Number(b) || 0), 0))
		 : 0;
	 
	   const zollarLabel = zCount > 0 ? qLabel(zCount, 100) : null;
	   const capitalLabel = !zollarLabel && amount ? qLabel(amount, 1000) : null;
	 
	   const giftText = zollarLabel
		 ? `${zollarLabel} Z$`
		 : (capitalLabel ? `${capitalLabel} capital` : 'some assets');
	 
	   msg = `<p><strong>${sender}</strong> wants to give you <strong>${giftText}</strong>.</p><p>Accept this generous gift?</p>`;
	   modalType = 'success';
	 } else if (type === 'loan') {
	 const vulnText = vulnerabilityReason ? `<div style="margin:16px 0;padding:12px;background:rgba(252,186,3,0.15);border:2px solid #fcba03;border-radius:8px;">
	   <p style="font-family:'bithigh',monospace;font-size:0.95em;color:#fcba03;margin-bottom:6px;font-weight:bold;">Their vulnerability disclosure:</p>
	   <p style="font-style:italic;color:#fff;font-size:1.05em;line-height:1.5;">"${vulnerabilityReason}"</p>
	 </div>` : '';

	 const zollarMag = abstractRequest?.zollarMagnitude || 0;
	 const capitalMag = abstractRequest?.capitalMagnitude || 0;
	 const requestMagnitude = getQualitativeDescriptor(Math.max(zollarMag, capitalMag), 100);

	 title = 'M Note Loan Request';
	 msg = `<p style="margin-bottom:12px;"><strong>${sender}</strong> is requesting a loan from you.</p>${vulnText}<p style="margin-top:12px;color:#999;font-size:0.9em;">Request magnitude: <strong style="color:#fcba03;">${requestMagnitude}</strong></p><p style="margin-top:8px;font-size:0.9em;color:#999;">Will you provide this loan?</p>`;
	 modalType = 'warning';
   } else if (type === 'cds') {
	 const pType = (principalType || (pkg && pkg.principalType) || 'capital').toLowerCase();
	 let offeredQty;
	 if (pType === 'zollars') {
	   const rawZ = pkg?.zollars;
	   if (typeof rawZ === 'object' && rawZ !== null && !Array.isArray(rawZ)) {
		 offeredQty = Object.values(rawZ).reduce((a, b) => a + (Number(b) || 0), 0);
	   } else {
		 const zCount = Array.isArray(rawZ) ? rawZ.length : Number(rawZ);
		 offeredQty = Number.isFinite(zCount) && zCount > 0 ? zCount : (Number(amount) || 0);
	   }
	 } else {
	   offeredQty = Number(amount) || 0;
	 }

	 const headline = pType === 'zollars'
	   ? `<strong>${qLabel(offeredQty, 100)} Ƶ</strong> available immediately!`
	   : `<strong>${offeredQty.toLocaleString()} capital</strong> available immediately!`;

	 title = 'Premium Funding Opportunity';
	 msg = `<p><strong>${sender}</strong> is offering you an <strong>exclusive Z-Payday Premium Package</strong>:</p>
	   <p style="font-size:1.1em;color:#3dd202;margin:10px 0;">${headline}</p>
	   <p style="color:#fcba03;">Fast-track financing with flexible terms</p>
	   <p style="color:#fcba03;">Quick access to capital when you need it most</p>
	   <p style="color:#fcba03;">Dynamic growth potential for your portfolio</p>
	   <p style="font-size:0.85em;color:#888;margin-top:12px;">Accept this limited-time opportunity?</p>`;
	 modalType = 'success';
   } else if (type === 'v_note') {
	 const vulnText = vulnerabilityReason ? `<div style="margin:16px 0;padding:12px;background:rgba(252,186,3,0.15);border:2px solid #fcba03;border-radius:8px;">
	   <p style="font-family:'bithigh',monospace;font-size:0.95em;color:#fcba03;margin-bottom:6px;font-weight:bold;">Their vulnerability disclosure:</p>
	   <p style="font-style:italic;color:#fff;font-size:1.05em;line-height:1.5;">"${vulnerabilityReason}"</p>
	 </div>` : '';
	 const severityText = (typeof vulnerabilityScore === 'number')
	   ? `<p style="font-size:0.9em;color:#999;margin-top:8px;">Situation severity: <strong style="color:#fcba03;">${qualitativeFromFraction_LOCAL(Math.max(0, Math.min(1, vulnerabilityScore)))}</strong></p>`
	   : '';

	 title = 'V Note Aid Request';
	 msg = `<p style="margin-bottom:12px;"><strong>${sender}</strong> is requesting aid from you and has disclosed their vulnerability:</p>${vulnText}${severityText}<p style="margin-top:12px;font-size:0.9em;color:#999;">Will you grant aid to this request?</p>`;
	 modalType = 'warning';
   } else {
	 title = 'Social Economy Request';
	 msg = `<p><strong>${sender}</strong> sent you a social economy request.</p>`;
	 modalType = 'warning';
   }

   showHoloConfirm({
	 title,
	 message: msg,
	 type: modalType,
	 acceptText: 'Accept',
	 declineText: 'Decline',
	 onAccept: () => {
	   socket.emit('socialEconomyResponse', {
		 accepted: true,
		 type,
		 fromName: sender,
		 amount,
		 package: pkg,
		 requesterId: data.requesterId,
		 sessionId: data.sessionId,
		 humanInterest: data.humanInterest,
		 vulnerabilityReason: data.vulnerabilityReason,
		 vulnerabilityScore: data.vulnerabilityScore,
		 principalType: data.principalType
	   });
	 },
	 onDecline: () => {
	   socket.emit('socialEconomyResponse', {
		 accepted: false,
		 type,
		 fromName: sender,
		 amount,
		 package: pkg,
		 requesterId: data.requesterId,
		 sessionId: data.sessionId,
		 humanInterest: data.humanInterest,
		 vulnerabilityReason: data.vulnerabilityReason,
		 vulnerabilityScore: data.vulnerabilityScore,
		 principalType: data.principalType
	   });
	 }
   });
 });

 // === Debt Instrument Events ===
 registerDebtPopupHandlers(socket);

 // === Social Economy Responses ===
 socket.on('socialEconomyResponse', (data) => {
   console.log('💬 [Frontend] Social economy response received!');
   console.log('💬 [Frontend] Data:', JSON.stringify(data, null, 2));

   const message = data.accepted
	 ? `${data.fromName} accepted your ${data.type} request.`
	 : `${data.fromName} declined your ${data.type} request.`;

   if (typeof modalManager !== 'undefined' && modalManager.showMessage) {
	 modalManager.showMessage(message, data.accepted, null);
   } else {
	 showNotification(message, data.accepted);
   }

   if (window.emotionalMeter) {
	 window.emotionalMeter.fetchData();
   }
 });

 socket.on('botVulnerabilityShared', (data) => {
   console.log('💭 Bot vulnerability message received:', data);
   const { botName, message, vulnerabilityType, emotionalContext } = data;
   const displayMessage = message || t('socketFront.botVulnerabilityDefault', '{botName} has shared something personal with you.')
	 .replace('{botName}', botName || 'A bot');

   if (typeof modalManager !== 'undefined' && modalManager.showMessage) {
	 modalManager.showMessage(displayMessage, true, null);
   } else {
	 showNotification(displayMessage, true);
   }

   if (window.emotionalMeter) {
	 window.emotionalMeter.fetchData();
   }
 });


socket.on('memento_mori_announced', (data) => {
	console.log('🏺 Memento Mori announced:', data);
	showMementoMoriAnnouncement(data);
  });
  
  socket.on('memento_mori_completed', (data) => {
	console.log('✅ Memento Mori completed:', data);
	showNotification(t('socketFront.mementoComplete', 'You have completed your contemplation of mortality.'), true);
  });
  
  socket.on('memento_mori_session_event', (data) => {
	console.log('🏺 Session event:', data);
	showNotification(data.message, true);
  });
  
  socket.on('memento_mori_symbolic_death', (data) => {
	console.log('💀 Symbolic death:', data);
	showNotification(data.message, false);
  });
  
 socket.on('memento_mori_price_update', (data) => {
   console.log('⚰️ Global stock price updated:', data);
   const { companyName, newPrice, systemCompanyId } = data;
   
   if (typeof updateStockPriceDisplay === 'function') {
	 updateStockPriceDisplay(systemCompanyId, newPrice);
   }
   
   showNotification(
	 `${companyName} value changed to ${newPrice.toFixed(2)} due to global trading`,
	 true
   );
   if (localStorage.getItem('simulationType') === 'memento-mori') updateEulogyValue();
 });
  
  socket.on('memento_mori_timer_expired', (data) => {
	console.log('⏰ Memento Mori timer expired for user:', data);
	const portfolioId = getPortfolioId();
	
	if (data.portfolioId === portfolioId) {
	  if (typeof handleMementoMoriTimeout === 'function') {
		handleMementoMoriTimeout();
	  }
	} else {
	  showNotification(
		`${data.portfolioName} has reached their symbolic death`,
		true
	  );
	}
  });
  
  socket.on('memento_mori_phase_change', (data) => {
	console.log('⚰️ Memento Mori phase change:', data);
	const portfolioId = getPortfolioId();
	
	if (data.portfolioId !== portfolioId) {
	  showNotification(
		`${data.portfolioName} entered ${data.newPhase} phase`,
		true
	  );
	}
  });
 
 socket.on('deletePortfolioUpdate', (data) => {
   displayDeletePortfolioUpdate(data);
 });

  socket.on('deletePortfolioButtonReceived', (data) => {
	const portfolioId = getPortfolioId();
	if (data.portfolioId === portfolioId) {
	  announceToolGranted('deletePortfolioButton');
	}
  });

socket.on('LBOButtonReceived', (data) => {
	const portfolioId = getPortfolioId();
	if (data.portfolioId === portfolioId) {
	  announceToolGranted('LBOButton');
	}
  });
  
socket.on('LBOUpdate', (data) => {
	if (data && data.message) {
	  handleLBOUpdateForTarget(data);
	}
  });

socket.on('SubsidyButtonReceived', (data) => {
	const portfolioId = getPortfolioId();
	if (data.portfolioId === portfolioId) {
	  announceToolGranted('SubsidyButton');
	}
  });
  
  socket.on('SubsidyUpdate', (data) => {
	if (data && data.message) {
	  handleSubsidyUpdateForTarget(data);
	}
  });
  
  socket.on('SubsidyReceived', (data) => {
	if (data && data.message) {
	  handleSubsidyReceived(data);
	}
  });

  socket.on('itemStolen', (data) => {
	if (data && data.message) {
	  handleItemStolen(data);
	}
  });

  socket.on('specialActionNotification', (data) => {
	if (data && data.message) {
	  handleSpecialActionNotification(data);
	}
  });

  socket.on('ransomButtonReceived', (data) => {
	const portfolioId = getPortfolioId();
	if (data.portfolioId === portfolioId) {
	  announceToolGranted('ransomButton');
	}
  });

  socket.on('RansomUpdate', (data) => {
	console.log('RansomUpdate received:', data);
	const portfolioId = getPortfolioId();
	if (data.portfolioId === portfolioId || !data.portfolioId) {
	  import('./ransom.js').then(({ showRansomNotification, disablePortfolioInteractions }) => {
		disablePortfolioInteractions();
		showRansomNotification(portfolioId, data.ransomBy, data.ransomPoints);
	  }).catch(err => {
		console.error('Failed to load ransom.js:', err);
	  });
	}
  });

  socket.on('ransomPaid', (data) => {
	console.log('Ransom paid notification received:', data);
	const { payerName, amount, payerId } = data;
	showNotification(t('socketFront.ransomPaidNotification', '{userName} has paid the ransom of {points} capital. The funds have been transferred to your account.')
	  .replace('{userName}', payerName || 'A trader')
	  .replace('{points}', amount || 0), true);
  });

 socket.on('communityInvitation', (data) => {
   const portfolioId = getPortfolioId();
   if (data.initiatorPortfolioId !== portfolioId) {
	 showCommunityInvitationPopup(data);
   }
 });
 
socket.on('communityJoinStatus', (data) => {
   if (data.success) {
	 startCommunityTimer(data.communityId);
	 showCommunityInterface(data.communityId);
	 showNotification(data.message, true);
   } else {
	 showNotification(`Error: ${data.message}`, false);
   }
 });

   socket.on('communityStarted', (data) => {
	 const { communityId, expirationTime } = data;
	 showCommunityInterface(communityId);  
	 startCommunityTimer(expirationTime); 
   });
 
   socket.on('communityUpdate', (data) => {
	 if (typeof handleCommunityUpdate === 'function') {
	   handleCommunityUpdate(data);
	 }
   });
 
   socket.on('communityCreationStatus', (data) => {
	 if (data.success) {
	   startCommunityTimer(data.communityId);
	   showCommunityInterface(data.communityId);
	   localStorage.setItem('activeCommunity', data.communityId);
	   showNotification(data.message, true);
	 } else {
	   showNotification(`Error: ${data.message}`, false);
	 }
   });
   
socket.on('createCommunityButtonAssigned', (data) => {
	 const portfolioId = getPortfolioId();
	 if (data.portfolioId === portfolioId) {
	   announceToolGranted('createCommunityButton');
	 }
   });

   socket.on('communityEnded', (data) => {
	 const { communityId } = data;
	 const communityTimer = document.getElementById('communityTimer');
	 const communityInterface = document.getElementById('communityInterface');
	 if (communityTimer) communityTimer.remove();
	 if (communityInterface) communityInterface.remove();
	 showNotification(t('socketFront.darkPoolEnded', 'The Dark Pool has ended. You are back to normal status.'), true);
   });
   
   socket.on('communityExpired', (data) => {
	 const { communityId } = data;
	 const communityTimer = document.getElementById('communityTimer');
	 const communityInterface = document.getElementById('communityInterface');
	 if (communityTimer) communityTimer.remove();
	 if (communityInterface) communityInterface.remove();
	 showNotification(t('socketFront.darkPoolExpired', 'The Dark Pool has expired. You are back to normal status.'), true);
   });

  socket.on('messageSent', (response) => {
	console.log('Message sent response:', response);
	if (response.success) {
	  showNotification(t('socketFront.messageSent', 'Message sent successfully!'), true);
	} else {
	  showNotification(response.message, false);
	}
  });

  socket.on('tradeCompleted', (data) => {
	console.log('✅ Trade completed event received:', data);

	const msg = data.message || t('socketFront.tradeAccepted', 'Trade accepted!');
	try {
	  localStorage.setItem('pendingNotification', JSON.stringify({
		message: msg,
		success: true,
		ts: Date.now()
	  }));
	} catch (_) {}

	if (window.cleanupOfferSentPopup) {
	  window.cleanupOfferSentPopup();
	}

	try { window.__removeWaitingPopup?.(); } catch {}
	document.getElementById('offerSentPopup')?.remove();
	document.getElementById('offerSentOverlay')?.remove();
	document.getElementById('waitingPopup')?.remove();
	localStorage.removeItem('pendingOffer');
	localStorage.removeItem('pendingWaitPopup');
  });

  socket.on('tradeDeclined', (data) => {
	console.log('❌ Trade declined event received:', data);

	const msg = data.message || t('socketFront.tradeDeclined', 'Trade declined.');
	try {
	  localStorage.setItem('pendingNotification', JSON.stringify({
		message: msg,
		success: false,
		ts: Date.now()
	  }));
	} catch (_) {}

	if (window.cleanupOfferSentPopup) {
	  window.cleanupOfferSentPopup();
	}

	try { window.__removeWaitingPopup?.(); } catch {}
	document.getElementById('offerSentPopup')?.remove();
	document.getElementById('offerSentOverlay')?.remove();
	document.getElementById('waitingPopup')?.remove();
	localStorage.removeItem('pendingOffer');
	localStorage.removeItem('pendingWaitPopup');
  });

  socket.on('tradeError', (data) => {
	console.error('❌ Trade error:', data);

	const msg = data.message || t('socketFront.tradeError', 'An error occurred with your trade offer');
	try {
	  localStorage.setItem('pendingNotification', JSON.stringify({
		message: msg,
		success: false,
		ts: Date.now()
	  }));
	} catch (_) {}

	if (window.cleanupOfferSentPopup) {
	  window.cleanupOfferSentPopup();
	}

	try { window.__removeWaitingPopup?.(); } catch {}
	document.getElementById('offerSentPopup')?.remove();
	document.getElementById('offerSentOverlay')?.remove();
	document.getElementById('waitingPopup')?.remove();
	localStorage.removeItem('pendingOffer');
	localStorage.removeItem('pendingWaitPopup');
  });

  socket.on('tradeExpired', (data) => {
	console.log('⏰ Trade expired:', data);

	const msg = data.message || t('socketFront.tradeExpired', 'Your trade offer has expired');
	try {
	  localStorage.setItem('pendingNotification', JSON.stringify({
		message: msg,
		success: false,
		ts: Date.now()
	  }));
	} catch (_) {}

	if (window.cleanupOfferSentPopup) {
	  window.cleanupOfferSentPopup();
	}

	try { window.__removeWaitingPopup?.(); } catch {}
	document.getElementById('offerSentPopup')?.remove();
	document.getElementById('offerSentOverlay')?.remove();
	document.getElementById('waitingPopup')?.remove();
	localStorage.removeItem('pendingOffer');
	localStorage.removeItem('pendingWaitPopup');
  });

 socket.on('tradeTimerExtended', (payload) => {
   const { initiatorUserId, targetUserId, newDuration } = payload || {};
   try {
	 if (typeof window.updateTradeTimerFromSocket === 'function') {
	   window.updateTradeTimerFromSocket(payload);
	 }
   } catch (e) {
	 console.warn('[socketU] tradeTimerExtended delegation failed:', e);
   }
   showNotification(`Timer extended to ${newDuration} seconds`, true);
 });
  
 socket.on('offerReceived', async (offerData) => {
   console.log('Offer received with data:', offerData);

	if (offerData.hasStory && offerData.tradeStory) {
	  console.log('Story included in offer:', offerData.tradeStory);
	}
  
	const isCurrentUserBot = localStorage.getItem('isBot') === 'true';

	if (isCurrentUserBot) {
	  console.log('🤖 Bot is the receiver. Auto-accepting offer from:', offerData.initiatorPortfolioName);
	  try {
		const { acceptTrade } = await import('./offer.js');
		await acceptTrade(offerData);
	  
		console.log('✅ Bot offer auto-accepted successfully');
		return;
	  } catch (error) {
		console.error('❌ Bot failed to auto-accept trade:', error);
		showNotification(
		  `${t('socketFront.tradeAcceptFailed', 'Failed to accept trade:')} ${error.message}`,
		  false
		);
	  }
	}
	showOfferPopup(offerData, localStorage.getItem('portfolioId'));
  });


  socket.on('buyOfferReceived', async (buyOfferData) => {
	console.log('Buy offer received with data:', buyOfferData);
	showReceiveBuyOfferPopup(buyOfferData);
  });

  socket.on('botTradeOffer', (payload) => {
	try {
	  showBotTradeOffer(payload);
	} catch (e) {
	  console.error('botTradeOffer handler error:', e);
	}
  });
  
  socket.on('narrativeResponse', (data) => {
	console.log('Received narrative response:', data);

	showNotification(`${data.response.fromName} ${t('socketFront.respondedToAnalysis', 'has responded to your trade analysis')}`, true);
	
	if (data.botResponse) {
	  setTimeout(() => {
		showUpdatedOfferPopup({
		  ...data.originalOffer,
		  narrativeExchange: data.narrativeExchange
		});
	  }, 2000);
	}
  });
  
  socket.on('narrativeResponseReceived', (payload) => {
	try {
	  const { originalOffer, narrativeExchange } = payload || {};
	  const offerForDialog = {
		botName: originalOffer?.botName,
		portfolioName: originalOffer?.portfolioName,
		points: originalOffer?.offerAmount,
		zollars: originalOffer?.zollarBid,
		itemName: originalOffer?.item_name || originalOffer?.itemName,
		isBot: !!originalOffer?.isBot,
		originalOffer,
		narrativeExchange
	  };
  
	  if (typeof window.showNarrativeResponseDialog === 'function') {
		window.showNarrativeResponseDialog(offerForDialog);
	  } else {
		const last = narrativeExchange?.[narrativeExchange.length - 1]?.message || '';
		showNotification(`Narrative reply from ${offerForDialog.portfolioName || offerForDialog.botName || 'Trader'}: ${last}`, true);
		console.log('[socketU] narrativeResponseReceived (fallback):', payload);
	  }
	} catch (err) {
	  console.error('[socketU] narrativeResponseReceived handler error:', err);
	}
  });
  
  socket.on('llmResponseReceived', (data) => {
	try {
	  if (typeof window.handleLLMResponse === 'function') {
		window.handleLLMResponse(data);
	  } else {
		showNotification('New response received.', true);
		console.log('[socketU] llmResponseReceived (fallback):', data);
	  }
	} catch (e) {
	  console.error('[socketU] llmResponseReceived handler error:', e);
	}
  });


  socket.on('botNarrativeResponse', (data) => {
	console.log('Bot narrative response received:', data);

	const existingPopup = document.getElementById('offerSentPopup') || document.getElementById('tradeOfferPopup');
	if (existingPopup) {
	  window.dispatchEvent(new Event('beforeunload'));

	  if (navigator.userAgent.includes('Firefox')) {
		document.dispatchEvent(new Event('visibilitychange'));
	  }

	  if (existingPopup.parentNode) {
		existingPopup.parentNode.removeChild(existingPopup);
	  } else {
		existingPopup.remove();
	  }

	  const overlay = existingPopup.parentElement;
	  if (overlay && overlay.style.position === 'fixed') {
		overlay.remove();
	  }
	}

	localStorage.removeItem('pendingOffer');
	showUpdatedOfferPopup({
	  ...data.originalOffer,
	  narrativeExchange: data.narrativeExchange,
	  hasLLMResponse: true,
	  llmResponse: data.botResponse
	});
  });

  socket.on('redeemOffer', (payload) => {
	try { showRedeemOfferPopup?.(payload); } catch (e) { console.error(e); }
  });

  socket.on('redeemResponse', (payload) => {
	try { handleRedeemResponse?.(payload); } catch (e) { console.error(e); }
  });

  socket.on('freebieZ1Event', (data) => {
	processMessage(data);
  });
  
  socket.on('giveItemSelect', (data) => {
	processMessage(data);
  });

  socket.on('givePoints', (data) => {
	processMessage(data);
  });

  socket.on('taxEventNotification', (data) => {
	processMessage(data);
  });

  socket.on('deductPoints', (data) => {
	processMessage(data);
  });
  
socket.on('displayArtwork', (data) => {
	  console.log('Received displayArtwork event:', data);
	  if (data.exhibitionId && data.artworkId) {
		  showDisplayedArtwork(data.exhibitionId, data.artworkId);
	  } else {
		  console.error('displayArtwork event received invalid data:', data);
	  }
  });
  
  socket.on('itemReceived', (data) => {
	  console.log('Received itemReceived event:', data);
	  processMessage(data);
	});

  socket.on('message', async (event) => {
	console.log('Raw message received on client:', event);
	if (isProcessingMessage) {
	  messageQueue.push(event);
	  return;
	}
	isProcessingMessage = true;

	try {
	  let processedData;
	  
	  if (event instanceof Blob) {
		const text = await event.text();
		console.log('Received message blob:', text);
		processedData = JSON.parse(text);
	  } else if (typeof event === 'string') {
		console.log('Received message:', event);
		processedData = JSON.parse(event);
	  } else if (typeof event === 'object') {
		console.log('Received JSON message object:', event);
		processedData = event;
	  } else {
		console.error('Received unknown message type:', typeof event);
		return;
	  }
	  
	  if (processedData && processedData.messageId) {
		socket.emit('messageAck', { messageId: processedData.messageId, received: true });
	  }
	  
	  processMessage(processedData);
	} catch (error) {
	  console.error('Error processing WebSocket message:', error);
	} finally {
	  isProcessingMessage = false;
	  if (messageQueue.length > 0) {
		const nextMessage = messageQueue.shift();

		if (nextMessage && nextMessage.messageId) {
		  socket.emit('messageAck', { messageId: nextMessage.messageId, received: true });
		}
		processMessage(nextMessage);
	  }
	}
  });

  socket.on('bidReceived', (data) => {
	try {
	  const me = getPortfolioId?.() || localStorage.getItem('portfolioId');
	  const target = data?.targetPortfolioId ?? data?.targetUserId;
	  if (target && target !== me) return;
	  
	  console.log('Bid received:', data);
	  const isMementoMori = localStorage.getItem('simulationType') === 'memento-mori';
	  if (isMementoMori && data.bidType === 'philosophical') {
		showMementoMoriBidPopup(data, me);
	  } else {
		showStockBidPopup(data, me);
	  }
	} catch (e) {
	  console.error('bidReceived handler error:', e);
	}
  });

  socket.on('waitingForResponse', (data) => {
	console.log('Waiting for response:', data);
	if (document.getElementById('bidSubmittingOverlay') || document.getElementById('waitingPopup')) return;
	if (typeof showWaitingForResponsePopup === 'function') {
	  showWaitingForResponsePopup(data?.message);
	}
  });

 socket.on('moodAnalysis', (data) => {
   try {
	 console.log('🧠 moodAnalysis event:', data);
	 if (!data || data.success === false) {
	   try { closeWaitingForResponsePopup?.(); } catch(_) {}
	   return;
	 }
	 const mood = data.analysis;
	 if (!mood) return;

	 const isResponder = window.__userIsInitiator === false;
	 if (!isResponder) {
	   try { sessionStorage.setItem('pendingMoodAnalysis', JSON.stringify(mood)); } catch(_) {}
	 } else {
	   try { sessionStorage.removeItem('pendingMoodAnalysis'); } catch(_) {}
	 }

	 try { closeWaitingForResponsePopup?.(); } catch(_) {}
   } catch (e) {
	 console.error('moodAnalysis handler error:', e);
   }
 });


  socket.on('bidAccepted', (data) => {
	try {
	  console.log('Bid accepted (initiator view):', data);
	  const { message, transactionId } = data;
	  const mood = data.moodResult || data.analysis;

	  const msg = message || t('socketFront.offerAcceptedLong', 'Your offer has been accepted!');
	  try {
		localStorage.setItem('pendingNotification', JSON.stringify({ message: msg, success: true, ts: Date.now() }));
	  } catch (_) {}
	  showNotification(msg, true);

	  const isRealUser = !localStorage.getItem('isBot');

	  if (mood && isRealUser) {
		try { sessionStorage.setItem('pendingMoodAnalysis', JSON.stringify(mood)); } catch(_) {}
	  }

	  console.log('Transaction accepted:', { transactionId, message });

	  try { closeWaitingForResponsePopup?.(); } catch (_) {}
	  try { document.getElementById('waitingPopup')?.remove(); } catch (_) {}
	  try { document.getElementById('bidSubmittingOverlay')?.remove(); } catch (_) {}

	} catch (e) {
	  console.error('bidAccepted handler error:', e);
	}
  });

  socket.on('bidDeclined', (data) => {
	try {
	  console.log('Bid declined (initiator view):', data);
	  const { message, transactionId } = data;
	  const mood = data.moodResult || data.analysis;

	  const msg = message || t('socketFront.offerDeclinedLong', 'Your offer has been declined!');
	  try {
		localStorage.setItem('pendingNotification', JSON.stringify({ message: msg, success: false, ts: Date.now() }));
	  } catch (_) {}
	  showNotification(msg, false);

	  const isRealUser = !localStorage.getItem('isBot');

	  if (mood && isRealUser) {
		try { sessionStorage.setItem('pendingMoodAnalysis', JSON.stringify(mood)); } catch(_) {}
	  }

	  console.log('Bid declined:', { transactionId, message });

	  try { closeWaitingForResponsePopup?.(); } catch (_) {}
	  try { document.getElementById('waitingPopup')?.remove(); } catch (_) {}
	  try { document.getElementById('bidSubmittingOverlay')?.remove(); } catch (_) {}

	} catch (e) {
	  console.error('bidDeclined handler error:', e);
	}
  });

  socket.on('bidAcceptSuccess', (data) => {
	try {
	  console.log('✅ Bid accept success (seller view):', data);
	  const { message } = data;
	  const mood = data.moodResult || data.analysis;

	  const msg = message || t('socketFront.offerAccepted', 'You accepted the bid.');
	  showNotification(msg, true);

	  const isRealUser = !localStorage.getItem('isBot');

	  if (mood && isRealUser) {
		try { showMoodAnalysisPopup(mood); } catch(e) { console.error('Error showing mood popup:', e); }
		try { sessionStorage.removeItem('pendingMoodAnalysis'); } catch(_) {}
	  }

	  try { closeWaitingForResponsePopup?.(); } catch (_) {}
	  try { document.getElementById('waitingPopup')?.remove(); } catch (_) {}
	  try { document.getElementById('bidSubmittingOverlay')?.remove(); } catch (_) {}

	} catch (e) {
	  console.error('bidAcceptSuccess handler error:', e);
	}
  });

  socket.on('bidDeclineSuccess', (data) => {
	try {
	  console.log('✅ Bid decline success (seller view):', data);
	  const { message, transactionId } = data;
	  const mood = data.moodResult || data.analysis;

	  const msg = message || t('socketFront.offerDeclined', 'You declined the bid.');
	  showNotification(msg, false);

	  const isRealUser = !localStorage.getItem('isBot');

	  if (mood && isRealUser) {
		try { showMoodAnalysisPopup(mood); } catch(e) { console.error('Error showing mood popup:', e); }
		try { sessionStorage.removeItem('pendingMoodAnalysis'); } catch(_) {}
	  }

	  console.log('Bid declined:', { transactionId, message });

	  try { closeWaitingForResponsePopup?.(); } catch (_) {}
	  try { document.getElementById('waitingPopup')?.remove(); } catch (_) {}
	  try { document.getElementById('bidSubmittingOverlay')?.remove(); } catch (_) {}

	} catch (e) {
	  console.error('bidDeclineSuccess handler error:', e);
	}
  });

  
  socket.on('bidClosed', (data) => {
	  console.log('Bid closed:', data);
	});
	
socket.on('bidExpired', (data) => {
	  console.log('Bid expired:', data);
	  showNotification(data.message || t('socketFront.offerExpired', 'Your offer has expired'), false);
	  try {
		if (document.getElementById('waitingPopup')) {
		  closeWaitingForResponsePopup?.(false);
		}
	  } catch (_) {}
	  try {
		if (window.shouldRedirectToMarket && window.shouldRedirectToMarket()) {
		  setTimeout(() => window.doMarketRedirectIfNeeded?.(), 1500);
		}
	  } catch (_) {}
	});

	socket.on('bidError', (data) => {
	  console.error('Bid error:', data);
	  showNotification(data.message || t('socketFront.offerError', 'An error occurred with your offer'), false);
	  try {
		if (document.getElementById('waitingPopup')) {
		  closeWaitingForResponsePopup?.(false);
		}
	  } catch (_) {}
	  try {
		if (window.shouldRedirectToMarket && window.shouldRedirectToMarket()) {
		  setTimeout(() => window.doMarketRedirectIfNeeded?.(), 1500);
		}
	  } catch (_) {}
	});

socket.on('transactionBurst', (data) => {
	console.log('🎉 Transaction burst triggered:', data);
	const { moodScore } = data;
	if (typeof moodScore === 'number') {
	  triggerBurst(moodScore);
	}
  });


  socket.on('stockShorted', (data) => {
	console.log('Stock shorted:', data);
	if (data.success) {
	  showNotification(t('socketFront.shortSuccess', 'Short position placed successful!'), true);
	} else {
	  showNotification(`${t('socketFront.shortFailed', 'Short position failed:')} ${data.message}`, false);
	}
  });
  
  socket.on('stockShortedNotification', (data) => {
	console.log('Owner notification:', data);
	showNotification(data.message, true);
  });
  
  socket.on('shortPositionClosed', (data) => {
	console.log('Short position closed:', data);
	showNotification(`${t('socketFront.shortClosedPrefix', 'Your short position on stock')} "${data.symbol}" ${t('socketFront.shortClosedSuffix2', 'has been automatically closed.')}`, true);
  });
  

 socket.on('bondBidReceived', (data) => {
   console.log('Bond bid received:', data);
   showBondBidPopup(data, getPortfolioId());
 });
 
 socket.on('bondBidAccepted', (data) => {
   console.log('Bond bid accepted:', data);
   showNotification(`${t('socketFront.bondBidPrefix', 'Your bid for bond')} "${data.bondName}" ${t('socketFront.bondAccepted', 'has been accepted.')}`, true);
 });
 
 socket.on('bondBidDeclined', (data) => {
   console.log('Bond bid declined:', data);
   showNotification(`${t('socketFront.bondBidPrefix', 'Your bid for bond')} "${data.bondName}" ${t('socketFront.bondDeclined', 'has been declined.')}`, false);
 });

 
 socket.on('bondBidActionSuccess', (data) => {
   console.log('Bond bid action success:', data);
   showNotification(data.message, data.success);
 });
 
 socket.on('bondBidError', (data) => {
   console.error('Bond bid error:', data.error);
   showNotification(`Error: ${data.error}`, false);
 });

  
  socket.on('bondMatured', (data) => {
	console.log('Bond matured:', data);
	showNotification(`${t('socketFront.yourBond', 'Your bond')} "${data.bondName}" ${t('socketFront.bondMatured', 'has matured, and zollars have been issued.')}`, true);
	displayPortfolioItems();
  });

  socket.on('forceLogout', () => {
	 showNotification(t('socketFront.loggedOutByAdmin', 'You have been logged out by the administrator.'), false);
	localStorage.removeItem('portfolioId');
	localStorage.removeItem('token');
	localStorage.removeItem('role');
	localStorage.removeItem('sessionId');
	localStorage.removeItem('socketId');
	localStorage.removeItem('initialPortfolioState');
	localStorage.removeItem('selectedItems');
	localStorage.removeItem('superUserId');
   
	document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
	document.cookie = 'sessionId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
	document.cookie = 'portfolioId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

   
	 document.cookie.split(";").forEach((cookie) => {
	   const name = cookie.split("=")[0];
	   document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
	 });
	 logout();
	 window.location.href = '/index.html';
   });
   
   socket.on('tokenExpired', () => {
	 showNotification("Session expired. Please log in again.", false);
	 logout();
	 setTimeout(() => {
	   window.location.href = '/index.html';
	 }, 2000);
   });


socket.on('playRandomPub', (data) => {
	console.log('Received playRandomPub event:', data);
	if (data && data.introVideoUrl && data.mainVideoUrl) {
	  playPubVideo(data.mainVideoUrl, data.introVideoUrl);
	} else {
	  console.error('Received playRandomPub event without valid video URLs:', data);
	}
  });
  
  socket.on('playRandomZnews', (data) => {
	console.log('Received playRandomZnews event:', data);
	if (data && data.introVideoUrl && data.mainVideoUrl) {
	  playZnewsVideo(data.mainVideoUrl, data.introVideoUrl);
	} else {
	  console.error('Received playRandomZnews event without valid video URLs:', data);
	}
  });
  
  // FIX: Store reference and clean up properly
  beforeUnloadHandler = () => {
	if (socket) {
	  socket.disconnect();
	}
  };
  window.addEventListener('beforeunload', beforeUnloadHandler);
  
  console.log('✅ [SocketU] All socket handlers attached');
}


function handleReconnect() {
  if (reconnectTimeout) {
	clearTimeout(reconnectTimeout); 
  }

  if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
	reconnectAttempts++;
	console.log(`Attempting to reconnect (${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})...`);
	reconnectTimeout = setTimeout(connectWebSocket, RECONNECT_DELAY * reconnectAttempts);
  } else {
	console.error('Max reconnection attempts reached. Please refresh the page.');
  }
}

 export function waitForSocketConnection() {
   return new Promise((resolve) => {
	 if (socket && socket.connected) {
	   resolve();
	 } else {
	   // FIX: Use once() to prevent listener accumulation
	   socket.once('connect', resolve);
	 }
   });
 }
 
function processMessage(data) {
	const portfolioId = getPortfolioId();

	console.log('Received JSON message:', data);
	console.log('Portfolio ID from cookie/localStorage:', portfolioId);
	console.log('Target Portfolio ID from message:', data.targetPortfolioId);

	if (data.action === 'offer' && data.targetPortfolioId === portfolioId) {
		console.log('Offer received, attempting to display popup:', data);
		showOfferPopup(data, portfolioId);
	} else if (data.action === 'socialTradeReceived') {
		showSocialTradeReceived(data);
	} else if (data.action === 'socialTradeResponse') {
		showSocialTradeResponse(data);
	} else if (data.action === 'taxEventNotification') {
		console.log('Tax Event Notification received, displaying tax decision popup.');
		showTaxDecisionPopup(portfolioId, data.pointAmount);

	} else if (data.action === 'zollarAssigned' && Array.isArray(data.portfolioIds) && data.portfolioIds.map(String).includes(portfolioId)) {
		window.__seenZollarEvents = window.__seenZollarEvents || new Set();
		if (data.eventId && window.__seenZollarEvents.has(data.eventId)) return;
		if (data.eventId) window.__seenZollarEvents.add(data.eventId);
		showZollarSelectedAssign(data.portfolioIds, data.message, data.amount);
		return;
	} else if (data.action === 'taxNotice' &&
		Array.isArray(data.portfolioIds) &&
		data.portfolioIds.map(String).includes(portfolioId)) {
		displayNotification(data.message || 'An asset was taxed by the Zederal Reserve.');
		return;
	} else if (data.action === 'freebieZ1') {
		console.log('Zederal Reserve Free Item Notification received, displaying popup.');
		displayNotification(data.message);

	} else if (data.action === 'pointsGivenNotice' &&
		Array.isArray(data.portfolioIds) &&
		data.portfolioIds.map(String).includes(portfolioId)) {
		showPointsAssignedNotification(data.portfolioIds, Number(data.points) || 0);
		return;
	} else if (data.action === 'pointsDeductedNotice' &&
		Array.isArray(data.portfolioIds) &&
		data.portfolioIds.map(String).includes(portfolioId)) {
		showPointsDeductedNotification(data.portfolioIds, Number(data.points) || 0, data.message);
		return;
	} else if (data.action === 'itemReceived') {
		showItemReceivedNotification(data.giverName, data.itemName);

	} else if (data.action === 'deductPoints' && Array.isArray(data.portfolioIds) && data.portfolioIds.includes(portfolioId)) {
		showPointsDeductedNotification(data.portfolioIds, data.points, data.message);
	} else if (data.action === 'giveItemSelect' && Array.isArray(data.portfolioIds) && data.portfolioIds.includes(portfolioId)) {
		showItemSelectedAssign(data.portfolioIds, data.message);

	} else if (data.action === 'givePoints' && Array.isArray(data.portfolioIds) && data.portfolioIds.includes(portfolioId)) {
		showPointsAssignedNotification(data.portfolioIds, data.points);

	} else if (data.targetPortfolioId === portfolioId) {
		if (data.action === 'makeBuyOffer') {
			console.log('Buy offer received, showing buy offer popup.');
			showReceiveBuyOfferPopup(data);
		} else if (data.action === 'rejectBuyOffer') {
			console.log('Buy offer rejected:', data);
			handleRejectBuyOffer(data);
		} else if (data.action === 'acceptBuyOffer') {
			console.log('Buy offer accepted:', data);
			handleBuyOfferAcceptedByOther(data);
		}
	}  else {
		console.log('Received message is not relevant to this user or action is not recognized.');
	}
}

export async function logout() {
	const portfolioId = localStorage.getItem('portfolioId');
	const token = localStorage.getItem('token');
	const isSimulated = localStorage.getItem('isSimulated') === 'true';

	if (!portfolioId || !token) {
		console.error('Portfolio ID or token not found in local storage');
		return;
	}

	try {
		if (isSimulated && socket && socket.connected) {
			console.log('🤖 Disconnecting bots for simulated user logout...');
			socket.emit('disconnectUserBots', { portfolioId });
			await new Promise(resolve => setTimeout(resolve, 1000));
		}

		const response = await fetch(`${apiBaseUrl}/api/portfolios/${portfolioId}`, {
			method: 'PATCH',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${token}`,
			},
			body: JSON.stringify({ active: false }),
		});

		if (!response.ok) {
			throw new Error('Failed to update active status during logout');
		}

	} catch (error) {
		console.error('Error updating active status during logout:', error);
		showNotification(t('socketFront.logoutActiveUpdateError', 'Error updating active status during logout. Please try again.'), false);
	}

	try {
		const logoutResponse = await fetch(`${apiBaseUrl}/api/logout`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${token}`,
			},
			body: JSON.stringify({ portfolioId, isSimulated }),
			credentials: 'include'
		});

		if (!logoutResponse.ok) {
			throw new Error('Failed to logout from server');
		}

		const logoutData = await logoutResponse.json();
		console.log(logoutData.message);
		
		if (isSimulated) {
			console.log('🎯 Simulation cleanup completed successfully');
		}
		
	} catch (error) {
		console.error('Error during server logout:', error);
		showNotification(t('socketFront.logoutServerError', 'Error during server logout. Please try again.'), false);
	}
	
	const simulationEnding = localStorage.getItem('simulationEnding') === 'true';
	
	if (isSimulated && socket && socket.connected && !simulationEnding) {
	  console.log('🤖 Disconnecting bots for simulated user logout...');
	  socket.emit('disconnectUserBots', { portfolioId });
	  await new Promise(resolve => setTimeout(resolve, 1000));
	}
	
	// Disconnect main socket
	if (socket && socket.connected) {
		console.log('🔌 Disconnecting main socket...');
		socket.disconnect();
	}
	
	const isMementoMori = localStorage.getItem('simulationType') === 'memento-mori';
	const isStorySimulation = localStorage.getItem('simulationType') === 'story-simulation';
	if (simulationEnding || isSimulated || isMementoMori) {

		try {
			document.getElementById('mortality-oracle-interface')?.remove();
			document.getElementById('reflection-interface')?.remove();
			document.getElementById('value-analysis-results')?.remove();
			document.getElementById('persistent-memento-timer')?.remove();
		  } catch (e) {
			console.warn('Error cleaning Memento Mori UI:', e);
		  }

	  cleanupSimulationTimer();
	  cleanupMementoMoriTimer();
	}

	if (isStorySimulation) {
		try {
			document.getElementById('story-bullet-points')?.remove();
			document.getElementById('story-phase-timer')?.remove();
		} catch (e) {
			console.warn('Error cleaning Story Simulation UI:', e);
		}
	}

	localStorage.removeItem('portfolioId');
	localStorage.removeItem('token');
	localStorage.removeItem('role');
	localStorage.removeItem('sessionId');
	localStorage.removeItem('socketId');
	localStorage.removeItem('initialPortfolioState');
	localStorage.removeItem('selectedItems');
	localStorage.removeItem('superUserId');
	localStorage.removeItem('isSimulated'); 
	localStorage.removeItem('simulatedCompany');
	localStorage.removeItem('firstLogin');
	localStorage.removeItem('activeMementoMori');
	localStorage.removeItem('mementoMoriChecked');
	localStorage.removeItem('mementoMoriStarted');
	localStorage.removeItem('simulationType');
	localStorage.removeItem('mementoMoriObjectiveId');
	localStorage.removeItem('mementoMoriMode');
	localStorage.removeItem('mementoShowAllCompanies');
	localStorage.removeItem('stockBidData');
	localStorage.removeItem('tradeStory');
	localStorage.removeItem('tradeStoryTimestamp');
	localStorage.removeItem('lastConnectedStableKey');
	localStorage.removeItem('simulationEndTime');
	localStorage.removeItem('simulationTimerId');
	localStorage.removeItem('simulationEnding');
	localStorage.removeItem('botTradeOffer');
	localStorage.removeItem('botTradeRecommendation');
	localStorage.removeItem('tradingRecommendation');
	localStorage.removeItem('botTradeTimestamp');
	localStorage.removeItem('storyId');
	localStorage.removeItem('storyTheme');
	localStorage.removeItem('storyPhase');
	localStorage.removeItem('storyPhaseEndsAt');

	for (let i = localStorage.length - 1; i >= 0; i--) {
	  const key = localStorage.key(i);
	  if (key && key.startsWith('trade_')) {
		localStorage.removeItem(key);
	  }
	}
	
	document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
	document.cookie = 'sessionId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
	document.cookie = 'portfolioId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

	window.location.href = '/index.html';
}

export function connectBotWebSocket(botToken, botPortfolioId, botSessionId) {
	console.log(`🤖 Connecting bot WebSocket for: ${botPortfolioId}`);
	return connectWebSocket(botToken, botPortfolioId, botSessionId);
}

export { socket, messageQueue, resetSocket };